// Collaborate.js
import React, { useRef, useLayoutEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "../styles/collaborate.css";
import {
  MdAssuredWorkload,
  MdSpeed,
  MdTrendingUp,
  MdHandshake,
} from "react-icons/md";
import Snackbar from "../components/snackbar";
import { useTranslation, Trans } from "react-i18next"; // 1. Import

gsap.registerPlugin(ScrollTrigger);

function Collaborate() {
  const { t } = useTranslation(); // 2. Initialize t function
  const statsSectionRef = useRef(null);
  const pageRef = useRef(null);
  const formRef = useRef(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "info",
    position: "bottom-center",
    duration: 4000,
  });

  const showSnackbar = (
    message,
    type = "info",
    position = "bottom-center",
    duration = 4000
  ) => {
    setSnackbar({ open: true, message, type, position, duration });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleCounter = (section) => {
    const counters = section.querySelectorAll(".stat-value-number");
    counters.forEach((counter) => {
      const target = +counter.getAttribute("data-target");
      const format = counter.getAttribute("data-format");
      gsap.killTweensOf(counter);
      counter.textContent = "0";
      gsap.to(counter, {
        textContent: target,
        duration: 2,
        ease: "power1.out",
        snap: { textContent: 1 },
        modifiers: {
          textContent: (value) => {
            const val = Math.ceil(value);
            return format === "k"
              ? `${Math.round(val / 1000)}k`
              : val.toLocaleString();
          },
        },
      });
    });
  };

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (statsSectionRef.current) {
        ScrollTrigger.create({
          trigger: statsSectionRef.current,
          start: "top 75%",
          onEnter: () => handleCounter(statsSectionRef.current),
        });
      }
    }, pageRef);
    return () => ctx.revert();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      name: formData.get("name"),
      email: formData.get("email"),
      phone: formData.get("phone"),
      landLocation: formData.get("landLocation"),
      description: formData.get("description"),
    };

    if (
      !data.name ||
      !data.email ||
      !data.phone ||
      !data.landLocation ||
      !data.description
    ) {
      showSnackbar(
        t("collaboratePage.snackbar.validationError"),
        "error",
        "top-center"
      );
      return;
    }

    showSnackbar(
      t("collaboratePage.snackbar.sending"),
      "info",
      "bottom-center",
      0
    );

    try {
      const response = await fetch(
        "https://ddmwrl2uut5q8.cloudfront.net/submit-collaborate",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }
      );
      const result = await response.json();

      if (response.ok && result.success) {
        showSnackbar(
          result.message || t("collaboratePage.snackbar.success"),
          "success",
          "top-right"
        );
        if (formRef.current) {
          formRef.current.reset();
        }
      } else {
        showSnackbar(
          result.message ||
            t("collaboratePage.snackbar.submissionError", {
              statusText:
                response.statusText ||
                t("collaboratePage.snackbar.defaultStatusText"),
            }),
          "error",
          "bottom-center",
          6000
        );
      }
    } catch (err) {
      console.error("Error submitting collaborate form:", err);
      showSnackbar(
        t("collaboratePage.snackbar.networkError"),
        "error",
        "bottom-center",
        6000
      );
    }
  };

  return (
    <>
      <div className="collaborate-page-redesigned" ref={pageRef}>
        <section className="collab-hero">
          <video
            className="collab-hero-video"
            autoPlay
            muted
            loop
            playsInline
            src="/background.mp4"
            type="video/mp4"
          />
          <div className="collab-hero-overlay"></div>
          <div className="container collab-hero-content">
            <div className="collab-hero-text">
              <h1>
                <Trans
                  i18nKey="collaboratePage.hero.title"
                  components={[<br />]}
                />
              </h1>
              <p>{t("collaboratePage.hero.subtitle")}</p>
            </div>
            <div className="collab-hero-form">
              <h3>{t("collaboratePage.hero.formTitle")}</h3>
              <form
                className="collab-form"
                onSubmit={handleSubmit}
                ref={formRef}
              >
                <input
                  type="text"
                  name="name"
                  placeholder={t("collaboratePage.hero.form.namePlaceholder")}
                  required
                />
                <input
                  type="email"
                  name="email"
                  placeholder={t("collaboratePage.hero.form.emailPlaceholder")}
                  required
                />
                <input
                  type="text"
                  name="phone"
                  placeholder={t("collaboratePage.hero.form.phonePlaceholder")}
                  required
                />
                <input
                  type="text"
                  name="landLocation"
                  placeholder={t(
                    "collaboratePage.hero.form.locationPlaceholder"
                  )}
                  required
                />
                <textarea
                  name="description"
                  placeholder={t(
                    "collaboratePage.hero.form.descriptionPlaceholder"
                  )}
                  rows="4"
                  required
                />
                <button type="submit">
                  {t("collaboratePage.hero.form.submitButton")}
                </button>
              </form>
            </div>
          </div>
        </section>

        <section className="collab-reasons">
          <div className="container">
            <div className="section-header">
              <h2>
                <Trans
                  i18nKey="collaboratePage.reasons.title"
                  components={[<span className="highlight" />]}
                />
              </h2>
              <p className="section-subtitle">
                {t("collaboratePage.reasons.subtitle")}
              </p>
            </div>
            <div className="reasons-grid">
              <div className="reason-card">
                <div className="icon-wrapper">
                  <MdAssuredWorkload className="reason-icon" />
                </div>
                <h4>{t("collaboratePage.reasons.cards.maximize.title")}</h4>
                <p>{t("collaboratePage.reasons.cards.maximize.description")}</p>
              </div>
              <div className="reason-card">
                <div className="icon-wrapper">
                  <MdSpeed className="reason-icon" />
                </div>
                <h4>{t("collaboratePage.reasons.cards.hassleFree.title")}</h4>
                <p>
                  {t("collaboratePage.reasons.cards.hassleFree.description")}
                </p>
              </div>
              <div className="reason-card">
                <div className="icon-wrapper">
                  <MdHandshake className="reason-icon" />
                </div>
                <h4>{t("collaboratePage.reasons.cards.secure.title")}</h4>
                <p>{t("collaboratePage.reasons.cards.secure.description")}</p>
              </div>
              <div className="reason-card">
                <div className="icon-wrapper">
                  <MdTrendingUp className="reason-icon" />
                </div>
                <h4>{t("collaboratePage.reasons.cards.boostEconomy.title")}</h4>
                <p>
                  {t("collaboratePage.reasons.cards.boostEconomy.description")}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="collab-stats" ref={statsSectionRef}>
          <div className="container">
            <div className="section-header">
              <h2>
                <Trans
                  i18nKey="collaboratePage.stats.title"
                  components={[<span className="highlight" />]}
                />
              </h2>
              <p className="section-subtitle">
                {t("collaboratePage.stats.subtitle")}
              </p>
            </div>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value-number" data-target="75">
                  0
                </div>
                <div className="stat-unit-label">
                  <Trans
                    i18nKey="collaboratePage.stats.cards.buildTime"
                    components={[<span className="stat-sublabel" />]}
                  />
                </div>
                <div className="stat-decoration"></div>
              </div>
              <div className="stat-card">
                <div className="stat-value-number" data-target="10">
                  0
                </div>
                <div className="stat-unit-label">
                  <Trans
                    i18nKey="collaboratePage.stats.cards.leaseTerm"
                    components={[<span className="stat-sublabel" />]}
                  />
                </div>
                <div className="stat-decoration"></div>
              </div>
              <div className="stat-card">
                <div className="stat-value-number" data-target="2000">
                  0
                </div>
                <div className="stat-unit-label">
                  <Trans
                    i18nKey="collaboratePage.stats.cards.projectSize"
                    components={[<span className="stat-sublabel" />]}
                  />
                </div>
                <div className="stat-decoration"></div>
              </div>
              <div className="stat-card">
                <div
                  className="stat-value-number"
                  data-target="20000"
                  data-format="k"
                >
                  0
                </div>
                <div className="stat-unit-label">
                  <Trans
                    i18nKey="collaboratePage.stats.cards.developingArea"
                    components={[<span className="stat-sublabel" />]}
                  />
                </div>
                <div className="stat-decoration"></div>
              </div>
              <div className="stat-card">
                <div className="stat-value-number" data-target="30">
                  0
                </div>
                <div className="stat-unit-label">
                  <Trans
                    i18nKey="collaboratePage.stats.cards.locations"
                    components={[<span className="stat-sublabel" />]}
                  />
                </div>
                <div className="stat-decoration"></div>
              </div>
            </div>
          </div>
        </section>

        <div className="collab-footer-note-redesigned">
          <div className="container">
            <p>
              <Trans
                i18nKey="collaboratePage.footer.contact"
                components={[<a href="mailto:partners@e-qera.com" />]}
              />
            </p>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        position={snackbar.position}
        duration={snackbar.duration}
        onClose={handleCloseSnackbar}
      />
    </>
  );
}

export default Collaborate;
